import {
	Button,
	Dialog,
	DialogActions,
	DialogClose,
	DialogContent,
	DialogPopup,
	DialogTitle,
	Link,
} from '@huntington/honeycomb';
import { ColorBaseCharcoal, ColorBaseGreyLight } from '@huntington/honeycomb-design';
import { Box, CircularProgress, Typography } from '@mui/material';
import DownloadIcon from '../../../assets/download-icon.svg?react';
import {
	CIRCADIA_HELP_EMAIL,
	DISCLOSURE_DOCUMENT_CONTINUE_BUTTON,
	DISCLOSURE_DOCUMENT_DOWNLOAD_BUTTON,
} from '../../../constants/messages';

export interface DocumentModalProps {
	/** The string (blob) containing the document to display. */
	data: string | undefined;
	/** The filename (excluding extension) for downloading. */
	filename: string;
	/** If true, displays an error message instead of pdf. */
	isError: boolean;
	/** If true, shows a loading spinner while the document is being fetched. */
	isLoading: boolean;
	/** Callback function for when the user closes the modal. */
	onClose: () => void;
	/** Title of the modal. */
	title: string;
}

const DocumentModal = ({
	data,
	filename,
	isError,
	isLoading,
	onClose,
	title,
}: DocumentModalProps) => {
	return (
		<Dialog defaultOpen onOpenChangeComplete={(open) => !open && onClose()}>
			<DialogPopup style={{ zIndex: 1200 }}>
				<DialogTitle>
					<DialogClose />
					{title}
				</DialogTitle>

				<DialogContent>
					<Box
						sx={{
							alignItems: 'center',
							border: `1px solid ${ColorBaseGreyLight}`,
							display: 'flex',
							height: '22rem',
							justifyContent: 'center',
							overflowY: 'auto',
							width: '30rem',
						}}
					>
						{isLoading ? (
							<CircularProgress size="5rem" color="primary" sx={{ margin: '0 auto' }} />
						) : isError ? (
							<Typography>
								Something went wrong trying to fetch the {title} PDF. Please try again or contact
								Circadia for support at{' '}
								<Link href={`mailto:${CIRCADIA_HELP_EMAIL}`}>{CIRCADIA_HELP_EMAIL}</Link>
							</Typography>
						) : data ? (
							<object
								data={data}
								type="application/pdf"
								width="100%"
								height="100%"
								data-testid="file-preview"
							>
								<Typography>
									Unable to load preview. Please use the link below to download the PDF.
								</Typography>
							</object>
						) : null}
					</Box>
				</DialogContent>

				<DialogActions style={{ justifyContent: 'space-between' }}>
					<Box sx={{ display: 'flex', gap: '0.5rem', visibility: data ? 'visible' : 'hidden' }}>
						<DownloadIcon height="1.5rem" width="1.25rem" color={ColorBaseCharcoal} />
						<Link href={data} download={`${filename}.pdf`} aria-disabled={!data}>
							{DISCLOSURE_DOCUMENT_DOWNLOAD_BUTTON}
						</Link>
					</Box>

					<Button autoFocus priority="primary" onClick={onClose}>
						{DISCLOSURE_DOCUMENT_CONTINUE_BUTTON}
					</Button>
				</DialogActions>
			</DialogPopup>
		</Dialog>
	);
};

export default DocumentModal;
